'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { globalAccounts } from '@/lib/data/global-accounts';
import { accountSubCategories, getCategoryLabel } from '@/lib/data/account-helpers';
import {
    FileStack,
    ArrowLeft,
    Save,
    X,
    ChevronDown,
    AlertCircle,
    CheckCircle2,
    Info,
    Lock,
    Sparkles
} from 'lucide-react';
import Link from 'next/link';

export default function AddAccountPage() {
    const router = useRouter();


    const schoolUnit = 'paud';
    const schoolName = "PAUD Al-Azhar 1";

    // Form State
    const [formData, setFormData] = useState({
        category: 'asset' as 'asset' | 'liability' | 'equity' | 'income' | 'expense',
        parentId: '',
        subCategory: '',
        name: '',
        description: '',
        status: 'active' as 'active' | 'inactive',
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Get parent accounts filtered by selected category
    const parentAccounts = useMemo(() => {
        return globalAccounts.filter(acc => !acc.parentId && acc.type === formData.category);
    }, [formData.category]);

    // Auto-generate code based on parent or category
    const autoGeneratedCode = useMemo(() => {
        if (formData.parentId) {
            // Child account - generate based on parent
            const parent = globalAccounts.find(acc => acc.id === formData.parentId);
            if (!parent) return '';

            // Find highest child number
            const children = globalAccounts.filter(acc => acc.parentId === formData.parentId);
            const childNumbers = children.map(child => {
                const parts = child.code.split('-');
                return parts.length > 1 ? parseInt(parts[1]) : 0;
            });
            const nextNumber = children.length > 0 ? Math.max(...childNumbers) + 1 : 1;

            return `${parent.code}-${String(nextNumber).padStart(3, '0')}`;
        } else {
            // Parent account - generate based on category
            const categoryPrefix = {
                'asset': '1',
                'liability': '2',
                'equity': '3',
                'income': '4',
                'expense': '5'
            }[formData.category];

            // Find highest parent code in this category
            const categoryParents = globalAccounts.filter(acc =>
                !acc.parentId && acc.type === formData.category
            );

            if (categoryParents.length === 0) {
                return `${categoryPrefix}101`; // First account in category
            }

            const parentCodes = categoryParents.map(acc => {
                const code = acc.code.replace(/\D/g, '');
                return parseInt(code);
            });
            const nextCode = Math.max(...parentCodes) + 1;

            return String(nextCode);
        }
    }, [formData.parentId, formData.category]);

    // Auto-determine balance type based on category
    const balanceType = useMemo(() => {
        return ['asset', 'expense'].includes(formData.category) ? 'debit' : 'credit';
    }, [formData.category]);

    // Get available sub-categories for selected category
    const availableSubCategories = useMemo(() => {
        return accountSubCategories[formData.category] || [];
    }, [formData.category]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => {
            const next = { ...prev, [field]: value };

            // Reset parentId and subCategory if category changes
            if (field === 'category') {
                next.parentId = '';
                next.subCategory = '';
            }

            return next;
        });

        // Clear error when user types
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Nama akun wajib diisi';
        }

        // Check if auto-generated code already exists
        if (globalAccounts.some(acc => acc.code === autoGeneratedCode)) {
            newErrors.code = 'Kode akun sudah digunakan. Sistem akan generate kode baru.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        // Determine subCategory value
        let finalSubCategory = formData.subCategory;

        // If parent exists, use parent's subCategory
        if (formData.parentId) {
            const parent = globalAccounts.find(acc => acc.id === formData.parentId);
            finalSubCategory = parent?.subCategory || formData.name;
        } else {
            // If no parent and no subCategory provided, use account name
            if (!finalSubCategory || finalSubCategory === '_custom') {
                finalSubCategory = formData.name;
            }
        }

        // Prepare data for submission
        const newAccount = {
            code: autoGeneratedCode,
            name: formData.name,
            category: formData.category,
            type: formData.category,
            subCategory: finalSubCategory,
            balanceType: balanceType,
            balance: 'Rp 0',
            status: formData.status,
            parentId: formData.parentId || undefined,
            description: formData.description || undefined,
            createdAt: new Date().toISOString().split('T')[0],
        };

        // Simulate API call
        setTimeout(() => {
            console.log('New Account Data:', newAccount);
            setIsSubmitting(false);
            setIsSuccess(true);

            setTimeout(() => {
                router.push('/sekolah/akun');
            }, 1500);
        }, 1000);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex items-center justify-between">
                <Link
                    href="/sekolah/akun"
                    className="group flex items-center gap-2 text-secondary hover:text-primary transition-all bg-surface px-4 py-2 rounded-xl border border-border-main shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Kembali ke List</span>
                </Link>

                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary">
                        <FileStack className="w-5 h-5" />
                    </div>
                </div>
            </div>

            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden relative">
                {/* Decorative background element */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary-soft/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="p-8 md:p-12 relative z-10">
                    <div className="mb-10 space-y-2">
                        <h1 className="text-3xl font-bold text-foreground tracking-tight">Tambah Akun Baru</h1>
                        <p className="text-secondary font-medium uppercase text-[10px] tracking-widest">
                            Master Data Global Yayasan • {schoolName}
                        </p>
                    </div>

                    {isSuccess ? (
                        <div className="py-20 flex flex-col items-center justify-center space-y-6 animate-in zoom-in duration-500">
                            <div className="w-20 h-20 bg-success-soft text-success rounded-full flex items-center justify-center shadow-lg shadow-success/20">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <div className="text-center space-y-2">
                                <h2 className="text-2xl font-bold text-foreground">Akun Berhasil Ditambahkan</h2>
                                <p className="text-secondary">Data akun sedang disimpan ke sistem...</p>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Info Alert */}
                            <div className="flex gap-4 p-5 bg-info-soft/20 rounded-2xl border border-info/10 text-info">
                                <Info className="w-5 h-5 shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                    <p className="text-[10px] font-bold uppercase tracking-widest">Panduan Pengisian</p>
                                    <ul className="text-xs font-medium leading-relaxed opacity-90 space-y-1 list-disc list-inside">
                                        <li>Kode akun akan <strong>otomatis di-generate</strong> oleh sistem</li>
                                        <li>Pilih kategori terlebih dahulu, lalu pilih akun induk (opsional)</li>
                                        <li>Sub kategori bersifat <strong>opsional</strong>, jika kosong akan menggunakan nama akun</li>
                                        <li>Parent dan child harus dalam <strong>kategori yang sama</strong></li>
                                        <li>Saldo awal selalu Rp 0 dan akan terisi dari transaksi</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Category Selection - REQUIRED, CANNOT CHANGE AFTER SAVE */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        Kategori Akun <span className="text-error">*</span>
                                        <span className="text-[9px] font-semibold text-warning bg-warning-soft px-2 py-0.5 rounded">Tidak bisa diubah setelah disimpan</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.category}
                                            onChange={(e) => handleChange('category', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-semibold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="asset">1. Aset (Assets) - Saldo Normal: Debit</option>
                                            <option value="liability">2. Liabilitas (Liabilities) - Saldo Normal: Credit</option>
                                            <option value="equity">3. Ekuitas (Equity) - Saldo Normal: Credit</option>
                                            <option value="income">4. Pendapatan (Income) - Saldo Normal: Credit</option>
                                            <option value="expense">5. Beban (Expenses) - Saldo Normal: Debit</option>
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                    <p className="text-xs text-muted font-medium">Pilih kategori sesuai dengan sifat akun dalam laporan keuangan</p>
                                </div>

                                {/* Parent Account Selection - OPTIONAL */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">
                                        Akun Induk / Parent (Opsional)
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.parentId}
                                            onChange={(e) => handleChange('parentId', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-medium focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="">-- Tidak ada parent (Akun Utama) --</option>
                                            {parentAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.code} - {acc.name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                    <p className="text-xs text-muted font-medium">
                                        {formData.parentId
                                            ? 'Akun ini akan menjadi sub-akun dari parent yang dipilih'
                                            : 'Kosongkan jika ini adalah akun utama (parent)'}
                                    </p>
                                </div>

                                {/* Auto-Generated Code - READ ONLY */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-success uppercase tracking-widest flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Kode Akun (Auto-Generated)
                                    </label>
                                    <div className="relative">
                                        <div className="px-5 py-3.5 bg-success-soft/20 border-2 border-success/20 rounded-2xl flex items-center justify-between">
                                            <p className="text-2xl font-black text-success tabular-nums tracking-wider">{autoGeneratedCode || '----'}</p>
                                            <div className="flex items-center gap-2 text-success">
                                                <Lock className="w-4 h-4" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest">Otomatis</span>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-xs text-success font-semibold">
                                        ✓ Kode ini di-generate otomatis oleh sistem dan tidak dapat diubah manual
                                    </p>
                                </div>

                                {/* Sub Category - OPTIONAL */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">
                                        Sub Kategori (Opsional)
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.subCategory === '_custom' ? '_custom' : formData.subCategory}
                                            onChange={(e) => {
                                                if (e.target.value === '_custom') {
                                                    handleChange('subCategory', '_custom');
                                                } else {
                                                    handleChange('subCategory', e.target.value);
                                                }
                                            }}
                                            className={`w-full px-5 py-3.5 bg-background border rounded-2xl text-sm font-semibold focus:ring-4 focus:ring-primary/10 transition-all outline-none appearance-none cursor-pointer ${errors.subCategory ? 'border-error focus:border-error' : 'border-border-main focus:border-primary'
                                                }`}
                                        >
                                            <option value="">-- Pilih Sub Kategori atau Kosongkan --</option>
                                            {availableSubCategories.map(subCat => (
                                                <option key={subCat} value={subCat}>
                                                    {subCat}
                                                </option>
                                            ))}
                                            <option value="_custom">+ Buat Sub Kategori Baru</option>
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                    {errors.subCategory && (
                                        <div className="flex items-center gap-2 text-error text-xs font-semibold">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            {errors.subCategory}
                                        </div>
                                    )}
                                    <p className="text-xs text-muted font-medium">
                                        Pilih dari daftar atau buat baru. Contoh: Kas, Bank, Piutang, Beban SDM
                                    </p>
                                </div>

                                {/* Custom Sub Category Input - Conditional */}
                                {formData.subCategory === '_custom' && (
                                    <div className="md:col-span-2 space-y-2">
                                        <label className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                            Nama Sub Kategori Baru <span className="text-error">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Masukkan nama sub kategori baru..."
                                            onChange={(e) => {
                                                // Store the custom value separately
                                                handleChange('subCategory', e.target.value || '_custom');
                                            }}
                                            className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-semibold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                                        />
                                        <p className="text-xs text-info font-medium">
                                            💡 Sub kategori baru akan ditambahkan ke daftar
                                        </p>
                                    </div>
                                )}

                                {/* Account Name - REQUIRED */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        Nama Akun <span className="text-error">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        placeholder="Contoh: Kas IDR (Petty Cash) atau Bank Mandiri Operasional"
                                        className={`w-full px-5 py-3.5 bg-background border rounded-2xl text-sm font-semibold focus:ring-4 focus:ring-primary/10 transition-all outline-none ${errors.name ? 'border-error focus:border-error' : 'border-border-main focus:border-primary'
                                            }`}
                                    />
                                    {errors.name && (
                                        <div className="flex items-center gap-2 text-error text-xs font-semibold">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            {errors.name}
                                        </div>
                                    )}
                                    <p className="text-xs text-muted font-medium">Gunakan nama yang jelas dan deskriptif</p>
                                </div>

                                {/* Description - OPTIONAL */}
                                <div className="md:col-span-2 space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">
                                        Deskripsi / Catatan (Opsional)
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        placeholder="Tambahkan catatan atau deskripsi akun jika diperlukan..."
                                        rows={3}
                                        className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-medium focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none resize-none"
                                    />
                                    <p className="text-xs text-muted font-medium">Contoh: "Digunakan untuk kas operasional harian unit"</p>
                                </div>

                                {/* Balance Type - AUTO DETERMINED */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest flex items-center gap-2">
                                        <Lock className="w-3 h-3" />
                                        Tipe Saldo Normal
                                    </label>
                                    <div className="px-5 py-3.5 bg-background border border-border-main rounded-2xl">
                                        <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider ${balanceType === 'debit'
                                            ? 'bg-success-soft text-success'
                                            : 'bg-error-soft text-error'
                                            }`}>
                                            {balanceType}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted font-medium">
                                        Otomatis ditentukan berdasarkan kategori akun
                                    </p>
                                </div>

                                {/* Status */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">
                                        Status Akun <span className="text-error">*</span>
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={formData.status}
                                            onChange={(e) => handleChange('status', e.target.value)}
                                            className="w-full px-5 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-semibold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="active">Aktif</option>
                                            <option value="inactive">Nonaktif</option>
                                        </select>
                                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                    <p className="text-xs text-muted font-medium">
                                        Akun nonaktif tidak dapat dipilih dalam transaksi baru
                                    </p>
                                </div>
                            </div>

                            {/* Summary Box */}
                            <div className="p-6 bg-background border border-border-main rounded-2xl space-y-4">
                                <h3 className="text-sm font-bold text-foreground uppercase tracking-widest">Ringkasan Akun Baru</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Kode</p>
                                        <p className="font-black text-primary text-lg tabular-nums">{autoGeneratedCode || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Kategori</p>
                                        <p className="font-bold text-foreground">{getCategoryLabel(formData.category)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Tipe Saldo</p>
                                        <p className="font-bold text-foreground uppercase">{balanceType}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Saldo Awal</p>
                                        <p className="font-bold text-muted">Rp 0</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-border-main flex flex-col md:flex-row items-center gap-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full md:w-auto px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-bold hover:shadow-2xl hover:shadow-primary/40 transition-all flex items-center justify-center gap-2 uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed group"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Memproses...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                            Simpan Akun
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => router.back()}
                                    className="w-full md:w-auto px-8 py-4 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold hover:bg-background transition-all flex items-center justify-center gap-2 uppercase tracking-widest"
                                >
                                    <X className="w-4 h-4" /> Batal
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>
        </main>
    );
}
